import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X, Upload, Star } from "lucide-react";

const EMOTION_TAGS = ["hope","courage","failure","growth","kindness","purpose","family","anxiety","grief"];
const TIERS = ["truth_keeper","truth_seeker","truth_maker"];

const emptyForm = () => ({
  title: "", ordinary_thing: "", lesson_summary: "", story_html: "",
  image_url: "", audio_url: "", audio_duration_sec: 0,
  wallpapers: { phone: "", desktop: "", tablet: "" },
  printable_url: "", printable_pdf_url: "", quote_collection_url: "",
  behind_scenes_video_url: "",
  emotion_tags: [], min_tier: "truth_keeper",
  is_exclusive: false, is_limited_edition: false,
  published: true, featured: false,
});

export default function AdminTruthReleases() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await http.get("/admin/truth-releases"); setItems(data); }
    catch { toast.error("Could not load releases"); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...emptyForm(), _new: true });
  const startEdit = (rel) => setEditing({ ...emptyForm(), ...rel, wallpapers: { ...emptyForm().wallpapers, ...(rel.wallpapers || {}) } });

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...editing }; delete payload._new;
      if (editing._new) await http.post("/admin/truth-releases", payload);
      else await http.put(`/admin/truth-releases/${editing.id}`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const remove = async (rel) => {
    if (!window.confirm(`Delete "${rel.title}"?`)) return;
    try { await http.delete(`/admin/truth-releases/${rel.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const toggleTag = (tag) => {
    const cur = editing.emotion_tags || [];
    setEditing({ ...editing, emotion_tags: cur.includes(tag) ? cur.filter((t) => t !== tag) : [...cur, tag] });
  };

  const uploadField = (key, kind) => async (file) => {
    if (!file) return;
    const route = kind === "audio" ? "/upload/audio" : "/upload/image";
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await http.post(route, { data_uri: reader.result, filename: file.name });
        if (key.startsWith("wallpapers.")) {
          const wkey = key.split(".")[1];
          setEditing((e) => ({ ...e, wallpapers: { ...e.wallpapers, [wkey]: data.url } }));
        } else {
          setEditing((e) => ({ ...e, [key]: data.url }));
        }
        toast.success("Uploaded");
      } catch (er) { toast.error(er?.response?.data?.detail || "Upload failed"); }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6" data-testid="admin-truth-releases">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div>
          <div className="font-display text-2xl text-[#F4F1EA]">Truth Releases</div>
          <div className="font-body text-[13px] text-[#8a857d] mt-1">
            Single source of truth for every member dashboard tab: Weekly Releases · Audio · Wallpapers · Printables · Archive · Exclusive. {items.length} total.
          </div>
        </div>
        <button onClick={startNew} data-testid="release-new-btn" className="px-4 py-2 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2">
          <Plus size={13}/> New Release
        </button>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a]">
        {items.length === 0 ? (
          <div className="p-10 text-center font-body text-[#8a857d] italic">No releases yet. Click <strong className="text-[#C9A055]">New Release</strong> to create your first weekly truth.</div>
        ) : (
          <table className="w-full">
            <thead className="text-[10px] tracking-[0.18em] text-[#8a857d] uppercase">
              <tr className="border-b border-white/10">
                <th className="text-left p-3">Title</th><th className="text-left p-3">Tier</th><th className="text-left p-3">Tags</th><th className="text-left p-3">Assets</th><th className="text-left p-3">★</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const assets = [r.image_url && "img", r.audio_url && "audio", (r.wallpapers?.phone || r.wallpapers?.desktop || r.wallpapers?.tablet) && "wall", (r.printable_url || r.printable_pdf_url) && "print", r.behind_scenes_video_url && "video"].filter(Boolean);
                return (
                  <tr key={r.id} className="border-b border-white/5" data-testid={`release-row-${r.id}`}>
                    <td className="p-3"><div className="font-body text-[14px] text-[#F4F1EA]">{r.title}</div><div className="font-ui text-[10px] text-[#8a857d]">{r.ordinary_thing}</div></td>
                    <td className="p-3 font-ui text-[10px] tracking-[0.22em] text-[#B5B0A7]">{r.min_tier?.replace("truth_","").toUpperCase()}</td>
                    <td className="p-3 font-body text-[11px] text-[#8a857d]">{(r.emotion_tags || []).join(", ") || "—"}</td>
                    <td className="p-3 font-ui text-[10px] text-[#5fc377]">{assets.length ? assets.join(" · ") : <span className="text-[#5a5650]">none</span>}</td>
                    <td className="p-3">{r.featured && <Star size={14} className="text-[#C9A055]" fill="#C9A055"/>}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => startEdit(r)} data-testid={`release-edit-${r.id}`} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#C9A055]"><Edit3 size={13}/></button>
                        <button onClick={() => remove(r)} data-testid={`release-delete-${r.id}`} className="p-1.5 border border-white/15 text-[#9a9690] hover:text-[#d97a7a]"><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#050505] border border-[#C9A055]/30 max-w-3xl w-full max-h-[94vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="release-modal">
            <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center">
              <div>
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">{editing._new ? "NEW RELEASE" : editing.id?.slice(0,8)}</div>
                <div className="font-display text-xl text-[#F4F1EA]">{editing._new ? "Create Release" : "Edit Release"}</div>
              </div>
              <button onClick={() => setEditing(null)} className="text-[#8a857d] hover:text-[#F4F1EA]"><X size={20}/></button>
            </div>

            <div className="p-6 space-y-5">
              <Field label="Title (e.g. 'The Match')" testid="rel-title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })}/>
              <Field label="Ordinary Thing (e.g. 'A Match')" testid="rel-ordinary" value={editing.ordinary_thing} onChange={(v) => setEditing({ ...editing, ordinary_thing: v })}/>
              <Field label="Lesson Summary (1-2 lines)" testid="rel-lesson" value={editing.lesson_summary} onChange={(v) => setEditing({ ...editing, lesson_summary: v })}/>
              <FieldTA label="Story HTML" testid="rel-story" value={editing.story_html} onChange={(v) => setEditing({ ...editing, story_html: v })} rows={6}/>

              <Uploader label="Cover image" url={editing.image_url} onUpload={uploadField("image_url", "image")} accept="image/*" testid="upl-image"/>
              <Uploader label="Audio (MP3 read by Mickey)" url={editing.audio_url} onUpload={uploadField("audio_url", "audio")} accept="audio/*" testid="upl-audio"/>

              <div className="border border-white/10 bg-black/40 p-3 space-y-3">
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">WALLPAPERS</div>
                <Uploader label="Phone Wallpaper" url={editing.wallpapers?.phone} onUpload={uploadField("wallpapers.phone", "image")} accept="image/*" testid="upl-phone"/>
                <Uploader label="Desktop Wallpaper" url={editing.wallpapers?.desktop} onUpload={uploadField("wallpapers.desktop", "image")} accept="image/*" testid="upl-desktop"/>
                <Uploader label="Tablet Wallpaper" url={editing.wallpapers?.tablet} onUpload={uploadField("wallpapers.tablet", "image")} accept="image/*" testid="upl-tablet"/>
              </div>

              <div className="border border-white/10 bg-black/40 p-3 space-y-3">
                <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">PRINTABLES</div>
                <Uploader label="Printable image (JPG/PNG)" url={editing.printable_url} onUpload={uploadField("printable_url", "image")} accept="image/*" testid="upl-print"/>
                <Field label="Printable PDF URL (paste a direct link)" testid="rel-printable-pdf" value={editing.printable_pdf_url} onChange={(v) => setEditing({ ...editing, printable_pdf_url: v })}/>
                <Field label="Quote Collection PDF URL" testid="rel-quote-pdf" value={editing.quote_collection_url} onChange={(v) => setEditing({ ...editing, quote_collection_url: v })}/>
              </div>

              <Field label="Behind-the-scenes video URL (YouTube/Vimeo, Seeker+)" testid="rel-bts" value={editing.behind_scenes_video_url} onChange={(v) => setEditing({ ...editing, behind_scenes_video_url: v })}/>

              <div>
                <Lbl>Emotion Tags</Lbl>
                <div className="flex flex-wrap gap-2">
                  {EMOTION_TAGS.map((t) => {
                    const on = editing.emotion_tags?.includes(t);
                    return (
                      <button key={t} type="button" onClick={() => toggleTag(t)} data-testid={`tag-${t}`}
                        className={`px-3 py-1 border font-ui text-[10px] tracking-[0.22em] uppercase ${on ? "border-[#C9A055] bg-[#C9A055] text-black" : "border-white/15 text-[#9a9690] hover:border-[#C9A055] hover:text-[#C9A055]"}`}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Lbl>Minimum Tier</Lbl>
                  <select className={inputCls} value={editing.min_tier} onChange={(e) => setEditing({ ...editing, min_tier: e.target.value })} data-testid="rel-min-tier">
                    {TIERS.map((t) => <option key={t} value={t}>{t.replace("truth_","")}</option>)}
                  </select>
                </div>
                <div className="flex flex-col gap-2 pt-5">
                  <Toggle label="Published" checked={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} testid="rel-published"/>
                  <Toggle label="Featured" checked={editing.featured} onChange={(v) => setEditing({ ...editing, featured: v })} testid="rel-featured"/>
                  <Toggle label="Exclusive Release" checked={editing.is_exclusive} onChange={(v) => setEditing({ ...editing, is_exclusive: v })} testid="rel-exclusive"/>
                  <Toggle label="Limited Edition (Truth Maker)" checked={editing.is_limited_edition} onChange={(v) => setEditing({ ...editing, is_limited_edition: v })} testid="rel-limited"/>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 border border-white/20 text-[#9a9690] font-ui text-[10px] tracking-[0.32em] uppercase">Cancel</button>
              <button onClick={save} disabled={busy || !editing.title} data-testid="release-save-btn" className="px-5 py-2 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase disabled:opacity-40">
                {busy ? "Saving…" : "Save Release"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-body text-[13px] focus:border-[#C9A055] focus:outline-none";
const Lbl = ({ children }) => <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d] mb-1 uppercase">{children}</div>;

function Field({ label, value, onChange, testid }) {
  return <label className="block"><Lbl>{label}</Lbl><input className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}/></label>;
}
function FieldTA({ label, value, onChange, testid, rows = 4 }) {
  return <label className="block"><Lbl>{label}</Lbl><textarea rows={rows} className={inputCls + " resize-y"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}/></label>;
}
function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="accent-[#C9A055]" data-testid={testid}/>
      <span className="font-ui text-[11px] tracking-[0.22em] text-[#B5B0A7] uppercase">{label}</span>
    </label>
  );
}

function Uploader({ label, url, onUpload, accept, testid }) {
  return (
    <div>
      <Lbl>{label}</Lbl>
      <div className="flex items-center gap-3 flex-wrap">
        {url ? (
          accept.startsWith("audio") ? (
            <audio controls src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} className="h-9"/>
          ) : (
            <img src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="w-16 h-16 object-cover border border-white/10"/>
          )
        ) : (
          <div className="w-16 h-16 border border-white/10 bg-black flex items-center justify-center text-[#5a5650] text-[10px]">none</div>
        )}
        <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#C9A055] text-[#C9A055] hover:bg-[#C9A055] hover:text-black font-ui text-[10px] tracking-[0.22em] uppercase cursor-pointer">
          <input type="file" accept={accept} className="hidden" onChange={(e) => onUpload(e.target.files?.[0])} data-testid={testid}/>
          <Upload size={11}/> Upload
        </label>
      </div>
    </div>
  );
}
